import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
    static targets = ["input", "preview", "bg", "icon", "clearBtn", "hiddenUrl"]
    static values = {
        uploadUrl: String // Endpoint to get the direct upload URL (e.g. /image_uploads/create_upload_url)
    }

    connect() {
        this.csrfToken = document.querySelector('meta[name="csrf-token"]').content

        // Show initial preview if we already have a URL (e.g. editing)
        if (this.hasHiddenUrlTarget && this.hiddenUrlTarget.value) {
            this.showPreview(this.hiddenUrlTarget.value)
        }
    }

    showPreview(url) {
        if (this.hasPreviewTarget) {
            if (this.previewTarget.tagName === "IMG") {
                this.previewTarget.src = url
            } else {
                this.previewTarget.style.backgroundImage = `url('${url}')`
            }
            this.previewTarget.classList.remove("hidden")
        }
        // Copie floutée derrière l'image entière (05/09/2026).
        if (this.hasBgTarget) { this.bgTarget.src = url; this.bgTarget.classList.remove("hidden") }
        if (this.hasIconTarget) this.iconTarget.classList.add("hidden")
        if (this.hasClearBtnTarget) this.clearBtnTarget.classList.remove("hidden")
    }

    async preview() {
        const file = this.inputTarget.files[0]
        if (!file) return

        // Show instant local preview while uploading
        const reader = new FileReader()
        reader.onload = (e) => {
            this.showPreview(e.target.result)
        }
        reader.readAsDataURL(file)

        // Start Cloudflare upload
        this.element.classList.add("opacity-50", "pointer-events-none")
        try {
            await this.uploadToCloudflare(file)
        } catch (error) {
            console.error("Cloudflare upload failed:", error)
            alert("Erreur lors de l'upload de l'image.")
            this.clear()
        } finally {
            this.element.classList.remove("opacity-50", "pointer-events-none")
        }
    }

    async uploadToCloudflare(file) {
        // 1. Get direct upload URL from our Rails server
        const endpoint = this.uploadUrlValue || '/image_uploads/create_upload_url'
        const response = await fetch(endpoint, {
            method: "POST",
            headers: {
                "X-CSRF-Token": this.csrfToken,
                "Accept": "application/json"
            }
        })

        if (!response.ok) throw new Error("Failed to get upload URL")

        const data = await response.json()
        if (!data.success) throw new Error(data.error)

        // 2. Upload file directly to Cloudflare
        const formData = new FormData()
        formData.append("file", file)

        // Cloudflare accepts direct uploads (no auth needed since the URL is a signed, one-time URL)
        const cfResponse = await fetch(data.upload_url, {
            method: "POST",
            body: formData
        })

        if (!cfResponse.ok) throw new Error("Failed to upload to Cloudflare")

        const cfData = await cfResponse.json()
        if (!cfData.success) throw new Error("Cloudflare rejected upload")

        // The CDN URL to access the image (find public variant or fallback to first)
        const result = cfData.result
        let imageDeliveryUrl = result.variants[0]
        const publicVariant = result.variants.find(v => v.endsWith('public'))
        if (publicVariant) imageDeliveryUrl = publicVariant

        // 3. Set the hidden input value to the CDN URL
        if (this.hasHiddenUrlTarget) {
            this.hiddenUrlTarget.value = imageDeliveryUrl
            // Fire a custom event for other controllers (like feed_composer)
            this.element.dispatchEvent(new CustomEvent("cf-upload:success", {
                detail: { url: imageDeliveryUrl },
                bubbles: true
            }))
        }
    }

    clear() {
        this.inputTarget.value = ""
        if (this.hasHiddenUrlTarget) this.hiddenUrlTarget.value = ""

        if (this.hasPreviewTarget) {
            if (this.previewTarget.tagName === "IMG") {
                this.previewTarget.src = ""
            } else {
                this.previewTarget.style.backgroundImage = ""
            }
            this.previewTarget.classList.add("hidden")
        }
        if (this.hasBgTarget) { this.bgTarget.src = ""; this.bgTarget.classList.add("hidden") }
        if (this.hasIconTarget) this.iconTarget.classList.remove("hidden")
        if (this.hasClearBtnTarget) this.clearBtnTarget.classList.add("hidden")

        this.element.dispatchEvent(new CustomEvent("cf-upload:cleared", { bubbles: true }))
    }
}
